/**
 * User Entity - Server-side API
 *
 * 서버 사이드(middleware, API routes)에서 사용하는 함수들입니다.
 * React 훅을 사용할 수 없는 환경에서 직접 Supabase 클라이언트를 받아 사용합니다.
 */
import type { TypedSupabaseClient } from '@popow/infra/lib/supabase/client';
import type { Tables } from '@popow/infra/types/database.types';

import { parseUserProfileWithTikTokRow, type UserProfileWithTikTok } from './schema';

/**
 * ID로 프로필을 조회합니다.
 * middleware에서 인증된 사용자의 프로필 확인에 사용됩니다.
 */
export async function fetchProfileById(
  supabase: TypedSupabaseClient,
  id: string,
): Promise<UserProfileWithTikTok | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error(`Error finding profile by id ${id}:`, JSON.stringify(error, null, 2));
    return null;
  }

  return data ? parseUserProfileWithTikTokRow(data as Tables<'user_profiles'>) : null;
}

/**
 * Referral code로 프로필을 조회합니다.
 */
export async function fetchProfileByReferralCode(
  supabase: TypedSupabaseClient,
  referralCode: string,
): Promise<UserProfileWithTikTok | null> {
  const { data, error } = await supabase
    .from('user_referrals')
    .select('id, user_profiles!inner (*)')
    .eq('referral_code', referralCode)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error finding profile by referral code:', error);
    throw error;
  }

  return data?.user_profiles
    ? parseUserProfileWithTikTokRow(data.user_profiles as unknown as Tables<'user_profiles'>)
    : null;
}

/**
 * 검색어로 프로필을 검색합니다.
 */
export async function searchProfiles(
  supabase: TypedSupabaseClient,
  term: string,
): Promise<UserProfileWithTikTok[]> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .or(`email.ilike.%${term}%,username.ilike.%${term}%`)
    .limit(10);

  if (error) {
    console.error('Error searching profiles:', error);
    return [];
  }

  return data.map(row => parseUserProfileWithTikTokRow(row as Tables<'user_profiles'>));
}

/**
 * 모든 프로필을 조회합니다. (Admin용)
 */
export async function fetchAllProfiles(
  supabase: TypedSupabaseClient,
): Promise<UserProfileWithTikTok[]> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching all profiles:', error);
    return [];
  }

  return data.map(row => parseUserProfileWithTikTokRow(row as Tables<'user_profiles'>));
}

/**
 * Admin용 검색 (id, email, username, tiktok_handle로 검색)
 */
export async function searchProfilesForAdmin(
  supabase: TypedSupabaseClient,
  query: string,
): Promise<UserProfileWithTikTok[]> {
  if (!query.trim()) {
    return fetchAllProfiles(supabase);
  }

  const escapedQuery = query.replace(/[%_]/g, '\\$&');
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .or(
      `id.ilike.%${escapedQuery}%,email.ilike.%${escapedQuery}%,username.ilike.%${escapedQuery}%,tiktok_handle.ilike.%${escapedQuery}%`,
    )
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error searching users:', error);
    return [];
  }

  return data.map(row => parseUserProfileWithTikTokRow(row as Tables<'user_profiles'>));
}
